import React, { useRef, useState } from "react"
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Stack, TextField, Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import api from "../../../../base/services/ResApi"
import OrgsDropdown from "../../../components/orgsDropdown/OrgsDropdown"
import GroupsDropdown from "../../../components/groupsDropdown/GroupsDropdown"
import TerminalImportResults from "./TerminalImportResults"

const errorMessage = (error) => error?.response?.data?.description ||
  "The request could not be completed. Check your connection and try again."

const BulkImportTerminalsModal = ({ open, onClose, setUpdateData, orgCode, grpId }) => {
  const [organization, setOrganization] = useState(orgCode || "")
  const [group, setGroup] = useState(grpId || "")
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState("")
  const requestInFlight = useRef(false)
  const [error, setError] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [importId, setImportId] = useState("")
  const [result, setResult] = useState(null)
  const [resultTitle, setResultTitle] = useState("")
  const [isErrorResult, setIsErrorResult] = useState(false)
  const [offset, setOffset] = useState("0")
  const [fileInputKey, setFileInputKey] = useState(0)

  const submit = async (event) => {
    event.preventDefault()
    if (requestInFlight.current || submitted) return
    if (!organization || !group || !file) {
      setError("Select an organization, a group, and a CSV (.csv) or Excel (.xlsx) file.")
      return
    }
    requestInFlight.current = true
    setBusy("upload")
    setError("")
    const form = new FormData()
    // The API deserializes this part as JSON, so it needs its own content type.
    form.append("additional_info", new Blob([
      JSON.stringify({ orgCode: organization, groupId: Number(group) }),
    ], { type: "application/json" }))
    form.append("terminals", new Blob([file], {
      type: /\.csv$/i.test(file.name) ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), file.name)
    try {
      const response = await api.post("/terminals/imports", form)
      if (!response) throw new Error("No response")
      setSubmitted(true)
      setResult(response.data)
      setIsErrorResult(false)
      setResultTitle("Upload summary")
      const id = response.headers?.["x-terminal-import-id"] || response.data?.importId || response.data?.data?.importId
      setImportId(typeof id === "string" ? id.trim() : "")
      setUpdateData(true)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      requestInFlight.current = false
      setBusy("")
    }
  }

  const checkResult = async (errors = false) => {
    if (requestInFlight.current || !submitted || !importId) return
    if (errors && (!/^\d+$/.test(offset) || !Number.isSafeInteger(Number(offset)))) {
      setError("Enter a non-negative whole number for the error offset.")
      return
    }
    requestInFlight.current = true
    setBusy(errors ? "errors" : "status")
    setError("")
    const headers = { "X-Terminal-Import-Id": importId.trim() }
    if (errors) Object.assign(headers, {
      "X-Action": "ERRORS", "X-Offset": Number(offset), "X-Limit": 50,
      "X-Sort-Column": "row_number", "X-Sort-Order": "ASC",
    })
    try {
      const response = await api.get("/terminals/check/import/results", { headers })
      if (!response) throw new Error("No response")
      setResult(response.data)
      setIsErrorResult(errors)
      setResultTitle(errors ? "Import errors" : "Import status")
      setUpdateData(true)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      requestInFlight.current = false
      setBusy("")
    }
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md" aria-labelledby="bulk-import-title">
      <DialogTitle className="createTerminalsTitle" id="bulk-import-title" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Bulk import terminals
        <IconButton aria-label="Close bulk import" disabled={!!busy} onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography>Upload a CSV (.csv) or Excel (.xlsx) terminal import file. All terminals in the file will use the selected organization and group.</Typography>
          <Stack component="section" aria-label="Sample import files" direction={{ xs: "column", sm: "row" }} spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }} sx={{ p: 2, borderRadius: 2, bgcolor: "#f0f7f6", border: "1px solid #dce7e7" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#234d50" }}>Need a template?</Typography>
              <Typography variant="body2" color="text.secondary">Download a sample, keep the column headers, and add your terminals before uploading.</Typography>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {[["CSV", "csv"], ["Excel", "xlsx"]].map(([label, extension]) => (
                <Button key={extension} component="a" variant="outlined" size="small" startIcon={<DownloadOutlinedIcon />}
                  href={`${process.env.PUBLIC_URL || ""}/templates/terminal-bulk-import-sample.${extension}`}
                  download={`Terminal Bulk Reg Sample.${extension}`} aria-label={`Download sample ${label}`}
                  sx={{ textTransform: "none", whiteSpace: "nowrap", bgcolor: "white", color: "#116c6f", borderColor: "#a6c9c7" }}>
                  {label} (.{extension})
                </Button>
              ))}
            </Stack>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {submitted && <Alert severity="info">Upload accepted. Check the import status below to confirm processing results before uploading the file again.</Alert>}
          <Box component="form" id="bulk-import-form" aria-label="Bulk terminal upload" onSubmit={submit}>
            <Stack spacing={3}>
              <Box sx={{ minHeight: 65, "& .MuiFormControl-root": { minWidth: 0, width: "100%" } }}>
                <OrgsDropdown orgCode={organization} isOrgDisabled={!!busy || submitted} onChange={(option) => {
                  setOrganization(option?.value || "")
                  setGroup("")
                }} />
              </Box>
              <Box sx={{ minHeight: 65, "& .MuiFormControl-root": { minWidth: 0, width: "100%" } }}>
                <GroupsDropdown key={organization} orgCode={organization} groupId={group}
                  isGrpDisabled={!organization || !!busy || submitted}
                  onChange={(option) => setGroup(option?.value || "")} />
              </Box>
              <TextField key={fileInputKey} type="file" label="Terminal import file" fullWidth
                disabled={!!busy || submitted} InputLabelProps={{ shrink: true }}
                inputProps={{ accept: ".csv,.xlsx" }}
                helperText={file ? `${file.name} (${Math.ceil(file.size / 1024)} KB)` : "Choose a .csv or .xlsx file in the terminal import format."}
                onChange={(event) => {
                  const selected = event.target.files?.[0]
                  setFile(null)
                  setError("")
                  if (!selected) return
                  if (!/\.(csv|xlsx)$/i.test(selected.name) || selected.size === 0) {
                    setError("Choose a non-empty CSV (.csv) or Excel (.xlsx) file.")
                    event.target.value = ""
                    return
                  }
                  setFile(selected)
                }} />
            </Stack>
          </Box>
          {submitted && <Box component="section" aria-label="Import progress" sx={{ p: 2, border: "1px solid #dce7e7", borderRadius: 2, bgcolor: "#f5f9f9" }}>
            <Typography variant="h6" gutterBottom>Import progress</Typography>
            <Typography variant="body2" color="text.secondary">Check the processing status or review errors for your uploaded file.</Typography>
            {!importId && <Alert severity="warning" sx={{ mt: 2 }}>The upload was accepted, but status tracking is unavailable. Contact support before uploading this file again.</Alert>}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }} sx={{ mt: 2 }}>
              <Button variant="outlined" disabled={!!busy || !importId.trim()} onClick={() => checkResult()}>Check status</Button>
              <TextField label="Error offset" type="number" size="small" value={offset}
                disabled={!!busy || !importId} inputProps={{ min: 0, step: 1 }}
                helperText="50 errors per request" onChange={(event) => setOffset(event.target.value)} />
              <Button variant="outlined" disabled={!!busy || !importId.trim()} onClick={() => checkResult(true)}>View errors</Button>
            </Stack>
          </Box>}
          {busy && <Stack direction="row" spacing={2} role="status"><CircularProgress size={22} /><Typography>{busy === "upload" ? "Uploading terminals…" : "Loading import results…"}</Typography></Stack>}
          {result !== null && <Box aria-live="polite"><Typography variant="h6" gutterBottom>{resultTitle}</Typography><TerminalImportResults value={result} errors={isErrorResult} /></Box>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button disabled={!!busy} onClick={onClose}>Close</Button>
        {submitted && <Button disabled={!!busy} onClick={() => {
          setSubmitted(false)
          setFile(null)
          setFileInputKey((value) => value + 1)
          setError("")
          setResult(null)
          setImportId("")
          setOffset("0")
        }}>Start another import</Button>}
        <Button type="submit" form="bulk-import-form" variant="contained" startIcon={<UploadFileIcon />}
          disabled={!!busy || submitted || !organization || !group || !file}>Upload terminals</Button>
      </DialogActions>
    </Dialog>
  )
}

export default BulkImportTerminalsModal
